import { useRouter } from 'next/router';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { isDebug } from '../lib/debug';

/**
 * True while the media query matches; false during server render. On the client the first
 * read is already the real value, so a component mounted later (a dialog) never paints one
 * frame with the wrong variant.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', notify);
      return () => mql.removeEventListener('change', notify);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** `?debug=1` on the current URL. */
export function useDebug(): boolean {
  const router = useRouter();
  return isDebug(router.query);
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export interface HotkeyOptions {
  enabled?: boolean;
  /** Fire even while the user is typing in a field (e.g. Ctrl K). */
  allowInInput?: boolean;
}

/**
 * Bind a keyboard shortcut such as `mod+k`, `mod+z`, `/`, `escape`.
 * `mod` is Ctrl on Windows/Linux and Cmd on macOS.
 */
export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  { enabled = true, allowInInput = false }: HotkeyOptions = {},
) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const parts = combo.toLowerCase().split('+');
    const key = parts[parts.length - 1];
    const wantMod = parts.includes('mod');
    const wantShift = parts.includes('shift');
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key) return;
      if (wantMod !== (e.metaKey || e.ctrlKey)) return;
      if (wantShift !== e.shiftKey) return;
      if (!allowInInput && isTypingTarget(e.target)) return;
      e.preventDefault();
      ref.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, enabled, allowInInput]);
}
